/**
 * Slate Violet Light Theme
 *
 * Cool light surfaces with violet primary actions.
 */
// Text tones the sidebar shares with the main ladder, declared once so raising
// one raises both. These were copied hex values, and a contrast fix to the
// ladder left the sidebar behind without anything failing.
const TEXT_HEADING = '#171339';

const slateVioletLightTheme = {
  name: 'slate-violet-light',
  label: 'Slate Violet Light',
  mode: 'light',
  colorSet: 'slate-violet',

  colors: {
    canvas: '#EDE9FE',
    panelPrimary: '#FFFFFF',
    panelSecondary: '#E5E0FC',
    panelTertiary: '#DDD6FE',
    panelQuaternary: '#D4CAFE',
    surfaceHover: '#D4CAFE',

    borderSubtle: '#C4B5FD',
    borderStrong: '#7C3AED',

    textHeading: TEXT_HEADING,
    textPrimary: '#1E1B4B',
    textSecondary: '#4C4A6F',
    textSupporting: '#5F5A82',
    textNeutral: '#605B7B',
    textMuted: '#605B7B',
    textInverse: '#FFFFFF',

    accentPrimary: '#7C3AED',
    accentPrimaryVibrant: '#6D28D9',
    accentSecondary: '#0D9488',
    accentSecondaryVibrant: '#0F766E',
    accentWarm: '#D97706',
    accentWarmVibrant: '#B45309',

    statusDanger: '#991B1B',
    statusWarning: '#92400E',
    statusSuccess: '#065F46',
    statusInfo: '#5B21B6',

    gridline: 'rgba(30, 27, 75, 0.10)',
    focusOutline: 'rgba(124, 58, 237, 0.42)',
    overlay: 'rgba(30, 27, 75, 0.40)',

    headerBg: '#FFFFFF',
    sidebarBg: '#E5E0FC',
    cardBg: '#FFFFFF',
    inputBg: '#FFFFFF',
    dropdownBg: '#FFFFFF',

    navSectionHeader: '#656082',
    navItemText: '#37335F',
    navItemHover: TEXT_HEADING,
    navItemHoverBg: 'rgba(124, 58, 237, 0.08)',
    navItemActive: '#6D28D9',
    navItemActiveBg: 'rgba(124, 58, 237, 0.13)',
    navIconDefault: '#656082',
    navIconHover: '#6D28D9',
    navIconActive: '#6D28D9',
    navDivider: 'rgba(107, 102, 138, 0.28)',

    tableEntity: '#1E1B4B',
    tableMetric: '#0F766E',
    tableBody: '#37335F',
    tableSecondary: '#4C4A6F',
    tableTime: '#5F5A82',

    qualityP20: '#991B1B',
    qualityP40: '#C2410C',
    qualityP60: '#92400E',
    qualityP80: '#047857',
    qualityP100: '#065F46',

    pillBgFrom: '#E5E0FC',
    pillBgTo: '#DDD6FE',
    pillActiveBgFrom: '#DDD6FE',
    pillActiveBgTo: '#D4CAFE',
    pillHoverBgFrom: '#DDD6FE',
    pillHoverBgTo: '#D4CAFE',

    tableHeaderBg: '#DDD6FE',
    tableRowHover: 'rgba(124, 58, 237, 0.07)',
    tableRowExpanded: 'rgba(13, 148, 136, 0.07)',
    buttonTextOnAccent: '#FFFFFF',

    statusDangerRgb: '153, 27, 27',
    statusSuccessRgb: '6, 95, 70',
    statusWarningRgb: '146, 64, 14',
    statusInfoRgb: '91, 33, 182',
    accentWarmRgb: '217, 119, 6',

    severityCriticalText: '#991B1B',
    severityCriticalBg: 'rgba(153, 27, 27, 0.10)',
    severityCriticalBorder: 'rgba(153, 27, 27, 0.24)',
    severityCriticalGlow: '0 0 18px rgba(153, 27, 27, 0.14)',
    severityHighText: '#92400E',
    severityHighBg: 'rgba(146, 64, 14, 0.10)',
    severityHighBorder: 'rgba(146, 64, 14, 0.24)',
    severityHighGlow: '0 0 18px rgba(146, 64, 14, 0.14)',
    severityMediumText: '#D97706',
    severityMediumBg: 'rgba(217, 119, 6, 0.10)',
    severityMediumBorder: 'rgba(217, 119, 6, 0.24)',
    severityMediumGlow: '0 0 18px rgba(217, 119, 6, 0.14)',
    severityLowText: '#5B21B6',
    severityLowBg: 'rgba(91, 33, 182, 0.10)',
    severityLowBorder: 'rgba(91, 33, 182, 0.22)',
    severityLowGlow: '0 0 18px rgba(91, 33, 182, 0.14)',
    severitySuccessText: '#065F46',
    severitySuccessBg: 'rgba(6, 95, 70, 0.10)',
    severitySuccessBorder: 'rgba(6, 95, 70, 0.22)',
    severitySuccessGlow: '0 0 8px rgba(6, 95, 70, 0.14)',

    borderMuted: '#DDD6FE',
    borderMedium: '#C4B5FD',
    borderAccent: '#7C3AED',

    surfaceSubtle: 'rgba(30, 27, 75, 0.035)',
    surfaceMid: 'rgba(30, 27, 75, 0.065)',
    surfaceOverlayHover: 'rgba(124, 58, 237, 0.065)',

    scrollbarTrack: 'rgba(221, 214, 254, 0.55)',
    scrollbarThumb: 'rgba(107, 102, 138, 0.55)',
    scrollbarThumbHover: 'rgba(76, 74, 111, 0.72)',

    selectionBg: 'rgba(124, 58, 237, 0.20)',

    graphBg: '#EDE9FE',
    graphNodeBg: '#FFFFFF',
    graphNodeText: '#1E1B4B',
  },

  shadows: {
    card: '0 1px 2px rgba(30, 27, 75, 0.06), 0 8px 20px rgba(30, 27, 75, 0.07)',
    soft: '0 2px 8px rgba(30, 27, 75, 0.06), 0 16px 32px rgba(30, 27, 75, 0.08)',
    dropdown: '0 8px 24px rgba(30, 27, 75, 0.14), 0 1px 3px rgba(30, 27, 75, 0.08)',
    drawer: '-8px 0 24px rgba(30, 27, 75, 0.10), -1px 0 3px rgba(30, 27, 75, 0.07)',
  },
};

export default slateVioletLightTheme;
